import { createContext, useContext } from 'react';

/**
 * フォームコンポーネントの内部状態管理について
 *
 * このコンポーネントではZustandではなくuseContextを採用
 *
 * 1. スコープ - フォームの状態はFormField以下のコンポーネントツリー内で完結
 * 2. パフォーマンス - 状態変更の影響範囲を最小限に抑制
 * 3. シンプルさ - 実装がシンプルで理解しやすく、テストも容易
 *
 * Zustandの採用を検討するケース：
 * - フォーム状態の永続化が必要な場合
 * - 複数フォーム間での状態共有が必要な場合
 * - フォーム状態のグローバル管理が必要な場合
 */

export type FormFieldContextValue = {
  name: string;
  error?: string;
  touched?: boolean;
  required?: boolean;
};

export const FormFieldContext = createContext<
  FormFieldContextValue | undefined
>(undefined);

export type FormItemContextValue = {
  id: string;
};

export const FormItemContext = createContext<FormItemContextValue | undefined>(
  undefined,
);

export const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  if (!itemContext) {
    throw new Error('useFormField should be used within <FormItem>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    error: fieldContext.error,
    touched: fieldContext.touched,
    required: fieldContext.required,
  };
};
